import React, { useEffect, useRef, useState } from 'react';
import { CaretDownFill } from 'react-bootstrap-icons';
import { Button, Typography } from '@ghq-abi/design-system-v2';

import { cn } from '~/lib/utils';

export const filterButtonActiveClassName = 'bg-[var(--primary-light,#FBF2B3)]';

export const filterButtonCaretIcon = (
  <CaretDownFill className="h-3 w-3 text-gray-600" aria-hidden />
);

const NEUTRAL_FILTER_VALUES = new Set<string | number>(['', 'all']);

export function isFilterValueActive(
  value: string | number | null | undefined
): boolean {
  return (
    value !== null && value !== undefined && !NEUTRAL_FILTER_VALUES.has(value)
  );
}

export interface DropdownOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  value?: string | number;
}

export interface DropdownButtonProps {
  text: string;
  options: DropdownOption[];
  onSelect?: (option: DropdownOption) => void;
  selectedValue?: string | number | null;
  disabled?: boolean;
  className?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  placeholder?: string;
  dropdownAlign?: 'left' | 'right';
}

const DropdownButton: React.FC<DropdownButtonProps> = ({
  text,
  options,
  onSelect,
  selectedValue,
  disabled = false,
  className,
  iconLeft,
  iconRight,
  size,
  placeholder = 'Select an option',
  dropdownAlign = 'left'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Encontrar opção selecionada baseada no valor
  useEffect(() => {
    if (selectedValue !== undefined) {
      const option = options.find(opt => opt.value === selectedValue);
      setSelectedOption(option || null);
    }
  }, [selectedValue, options]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option: DropdownOption) => {
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  const displayText = text;
  const isActive = isFilterValueActive(selectedValue);
  const resolvedIconRight = iconRight ?? filterButtonCaretIcon;

  return (
    <div ref={dropdownRef} className="relative">
      <Button
        variant="secondary"
        size={size as 'sm' | 'md' | 'lg'}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        border="default"
        className={cn(
          'border border-gray-200',
          isActive && filterButtonActiveClassName,
          className
        )}>
        <span className="flex items-center gap-2">
          {iconLeft ? <span className="flex-shrink-0">{iconLeft}</span> : null}

          <Typography variant="metadata-sm-medium" color="default">
            {displayText}
          </Typography>

          {resolvedIconRight ? (
            <span className="flex-shrink-0">{resolvedIconRight}</span>
          ) : null}
        </span>
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            'absolute top-full mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-50',
            dropdownAlign === 'right' ? 'right-0' : 'left-0'
          )}>
          <div className="py-1">
            {options.length > 0 ? (
              options.map(option => (
                <button
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center gap-3',
                    selectedOption?.id === option.id &&
                      'bg-blue-50 text-blue-700'
                  )}>
                  {/* Ícone da opção */}
                  {option.icon && (
                    <div className="flex-shrink-0">{option.icon}</div>
                  )}

                  {/* Texto da opção */}
                  <Typography
                    variant="metadata-sm-medium"
                    color="default"
                    className={
                      selectedOption?.id === option.id ? 'text-blue-700' : ''
                    }>
                    {option.label}
                  </Typography>
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500">
                <Typography variant="metadata-sm-medium" color="light">
                  {placeholder}
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownButton;
